import { useEffect, useState } from "react";

import {
  Divider,
  Grid,
  Button,
  Card,
  TablePagination,
  TextField,
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Rating,
  CardHeader,
  CardContent,
  CardActions,
  Collapse,
  Container,
  Box,
  Typography,
} from "@mui/material";

import _ from "lodash";
import moment from "moment";

import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import MoodIcon from "@mui/icons-material/Mood";
import RemoveShoppingCartIcon from "@mui/icons-material/RemoveShoppingCart";
import PendingIcon from "@mui/icons-material/Pending";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import BackpackIcon from "@mui/icons-material/Backpack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TagIcon from "@mui/icons-material/Tag";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import axiosInstance from "configs/axiosConfig";
import { APIRouteConstants } from "constants/routeConstants";
import Loader from "components/Loader";
import CustomLayout from "views/CustomLayout";
import CustomBreadcrumbs from "components/Breadcrumbs";
import CustomDialog from "components/Dialog";
import { GLOBAL_CURRENCY } from "constants/appConstants";
import CustomStepper from "components/Stepper";

let formatCurrency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: GLOBAL_CURRENCY.code,
  currencyDisplay: "narrowSymbol",
  minimumFractionDigits: 2,
});

const showName = (args) => {
  return _.compact(args).join(" ");
};

const showAddress = (address) => {
  return (
    <Box sx={{ p: { xs: 2, md: 3 }, background: "#f9fafb" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 2,
        }}
      >
        <LocationOnOutlinedIcon sx={{ fontSize: 18, color: "#374151" }} />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: "#111827" }}>
          Shipping Address
        </Typography>
      </Box>

      <Box
        sx={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: "10px",
          p: { xs: 2, md: 2.5 },
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        {/* Name */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PersonOutlineIcon sx={{ fontSize: 16, color: "#6b7280" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 500 }}>
              Full Name
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontWeight: 600, textTransform: "capitalize" }}>
              {showName([address.first_name, address.last_name]) || "—"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "#f3f4f6" }} />

        {/* Phone */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PhoneOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 500 }}>
              Phone
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontWeight: 500 }}>
              +{showName([address.country_code, address.phone_number]) || "—"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: "#f3f4f6" }} />

        {/* Address */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "8px",
              background: "#f3f4f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              mt: "2px",
            }}
          >
            <HomeOutlinedIcon sx={{ fontSize: 16, color: "#6b7280" }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", fontWeight: 500, mb: 0.3 }}>
              Address
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontWeight: 500, lineHeight: 1.6 }}>
              {address.street_address}
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", lineHeight: 1.6 }}>
              {[address.city, address.state].filter(Boolean).join(", ")}
            </Typography>
            <Typography sx={{ fontSize: "0.82rem", color: "#6b7280", lineHeight: 1.6 }}>
              {[address.country, address.pin].filter(Boolean).join(" - ")}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const steps = [
  {
    label: "Order Placed",
    id: 1,
    statusId: 1,
    icon: <PendingIcon />,
  },
  {
    label: "Order Confirmed",
    id: 2,
    statusId: 2,
    icon: <ThumbUpAltIcon />,
  },
  {
    label: "Order Packed",
    id: 3,
    statusId: 3,
    icon: <BackpackIcon />,
  },
  {
    label: "Order Shipped",
    id: 4,
    statusId: 4,
    icon: <LocalShippingIcon />,
  },
  {
    label: "Order Delivered",
    id: 5,
    statusId: 5,
    icon: <CheckIcon />,
  },
];

const failSteps = [
  {
    label: "Order Failed",
    id: 1,
    statusId: 1,
    icon: <CloseIcon />,
  },
  {
    label: "Order Confirmed",
    id: 2,
    statusId: 2,
    icon: <ThumbUpAltIcon />,
  },
  {
    label: "Order Packed",
    id: 3,
    statusId: 3,
    icon: <BackpackIcon />,
  },
  {
    label: "Order Shipped",
    id: 4,
    statusId: 4,
    icon: <LocalShippingIcon />,
  },
  {
    label: "Order Delivered",
    id: 5,
    statusId: 5,
    icon: <CheckIcon />,
  },
];

const cancelSteps = [
  {
    label: "Order Placed",
    id: 1,
    statusId: 1,
    icon: <PendingIcon />,
  },
  {
    label: "Order Confirmed",
    id: 2,
    statusId: 2,
    icon: <ThumbUpAltIcon />,
  },
  {
    label: "Order Packed",
    id: 3,
    statusId: 3,
    icon: <BackpackIcon />,
  },
  {
    label: "Cancelled",
    id: 4,
    statusId: 10,
    icon: <CloseIcon />,
  },
];

const getStatusChipStyle = (status = "") => {
  const s = (status || "").toLowerCase();
  if (s.includes("fail") || s.includes("cancel"))
    return { background: "#fdecea", color: "#c62828" };
  return { background: "#e6f4ea", color: "#2e7d32" };
};

const StatusStepper = ({ step, type }) => {
  return (
    <div className="status-wrapper">
      <CustomStepper
        steps={
          type == "cancel" ? cancelSteps : type == "fail" ? failSteps : steps
        }
        activeStep={type == "fail" ? 1 : step}
        completedStep={type == "fail" ? 1 : step}
      />
    </div>
  );
};

const OrderHistory = ({ breadcrumbs }) => {
  const [loader, setLoader] = useState(false);
  const [allOrders, setOrderList] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [addReview, addReviewToggle] = useState(false);
  const [selectedOrder, selectOrder] = useState("");
  const [reviewString, addReviewComment] = useState("");
  const [rating, setRating] = useState(0);
  const [showEmpty, setShowEmpty] = useState(false);

  const [isWebsiteFunctional, setIsWebsiteFunctional] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState(""); // NEW state

  const getWebsiteInfo = async () => {
    setLoader(true);
    try {
      const res = await axiosInstance.get(APIRouteConstants.DASHBOARD.WEB_INFO);
      const { data } = res;

      // Update both states from the response
      setIsWebsiteFunctional(data.status); // adjust if data.data.status
      setCurrencySymbol(data.currency_symbol || "₹"); // default if missing
    } catch (err) {
      console.error(err);
      setIsWebsiteFunctional(false);
      setCurrencySymbol("₹"); // fallback symbol
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    getWebsiteInfo();
  }, []);

  const getOrderList = async (category) => {
    setLoader(true);
    let queryParams = {
      page: pageCount + 1,
    };
    let res = await axiosInstance.get(
      APIRouteConstants.DASHBOARD.GET_ALL_ORDERS,
      {
        params: queryParams,
      },
    );
    let { data } = res;

    if (data && data.results) {
      if (data.count === 0) setShowEmpty(true);
      setOrderList(data.results);
      setTotalCount(data.count);
      setPageCount(data.current_page - 1);
    }
    setLoader(false);
  };

  useEffect(() => {
    getOrderList();
  }, [pageCount]);

  const handleChangePage = (e, newPage) => {
    setPageCount(newPage);
  };

  const DialogHeader = (
    <span className="title">
      Add Review - {_.get(selectedOrder, "product_name")}
    </span>
  );

  const DialogAction = (
    <>
      <Button type="submit" color="success" variant="contained">
        Add
      </Button>
      <Button type="submit" color="success" variant="contained">
        Cancel
      </Button>
    </>
  );

  const [collapseDiv, showCollapseDiv] = useState(false);
  const [displayOrder, setDisplayOrder] = useState();
  const [isShippingAddress, showShippingAddress] = useState(false);
  const [isStatus, showStatus] = useState(false);

  const resetCollapseDiv = () => {
    showCollapseDiv(false);
    showStatus(false);
    showShippingAddress(false);
    setDisplayOrder();
  };

  return (
    <CustomLayout>
      <Loader open={loader} />
      <Container maxWidth="lg">
        <Box
          sx={{
            width: "100%",
            margin: "0 auto",

            mt: {
              xs: "160px",
              md: "90px",
            },
          }}
        >
          <div className="content-containe" style={{ marginTop: "20px" }}>
            <CustomBreadcrumbs list={breadcrumbs} name={"Orders"} />

            <Grid
              container
              className="orderhistory-wrapper main-content-wrapper"
            >
              {showEmpty ? (
                <Card className="emptylist-wrapper">
                  <RemoveShoppingCartIcon />
                  <span className="title">No orders yet</span>
                  <Button className="btn" href="/products" variant="contained">
                    Go to Shop
                  </Button>
                </Card>
              ) : (
                <Grid container className="list-wrapper" mt={3}>
                  <Grid item xs={12} sm={12} md={12} lg={12} xl={8}>
                    {allOrders.map((order, i) => (
                      <Box
                        key={order.id || i}
                        sx={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "6px",
                          mb: 3,
                          overflow: "hidden",

                          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                          "&:hover": {
                            boxShadow: "0 4px 10px rgba(0,0,0,0.12)",
                          },
                        }}
                      >
                        {/* <CardHeader
                          className="header-detail"
                          title={
                            <Grid container>
                              <Grid
                                item
                                xs={8}
                                sm={8}
                                md={3}
                                className="detail-wrapper"
                              >
                                <span className="title">Order Placed</span>
                                <br />
                                <span className="data">
                                  {moment(order.created).format(
                                    "On ddd[,] D MMM, YYYY",
                                  )}
                                </span>
                              </Grid>
                              <Grid
                                item
                                xs={4}
                                sm={4}
                                md={3}
                                className="align-end detail-wrapper"
                              >
                                <span className="title">Total Price</span>
                                <span className="data">
                                  {currencySymbol} {order.final_price || 0}
                                </span>
                              </Grid>
                              <Grid
                                item
                                xs={7}
                                sm={7}
                                md={3}
                                className="detail-wrapper"
                              >
                                <span className="title">Order Id</span>
                                <br />
                                <span className="data"># {order.order_id}</span>
                              </Grid>
                              <Grid
                                item
                                xs={5}
                                sm={5}
                                md={3}
                                className="align-end detail-wrapper"
                              >
                                <span className="title">Transaction Id</span>
                                <span className="data">
                                  # {order.transaction_id}
                                </span>
                              </Grid>
                            </Grid>
                          }
                        ></CardHeader> */}
                        <Box
                          sx={{
                            borderBottom: "1px solid #e5e7eb",
                            px: 1.5,
                            py: 1.5,
                            background: "#fff",
                          }}
                        >
                          <Grid
                            container
                            alignItems="flex-start"
                            wrap={{ xs: "nowrap", md: "wrap" }}
                            sx={{ gap: 1 }}
                          >
                            {/* Order Placed */}
                            <Grid item xs md={3}>
                              <Typography
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.7rem" },
                                  color: "#374151",
                                  fontWeight: 600, // 👈 bold label
                                }}
                              >
                                Order Placed
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                  flexWrap: "nowrap",
                                }}
                              >
                                <CalendarTodayIcon
                                  sx={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: { xs: "0.5rem", md: "0.75rem" },
                                    color: "#6b7280",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {moment(order.created).format("D MMM YYYY, hh:mm A")}
                                </Typography>
                              </Box>
                            </Grid>

                            {/* Total Price */}
                            <Grid item xs md={3}>
                              <Typography
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.7rem" },
                                  color: "#374151",
                                  fontWeight: 600,
                                }}
                              >
                                Total Price
                              </Typography>

                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <ReceiptLongIcon
                                  sx={{ fontSize: 12, color: "#9ca3af" }}
                                />
                                <Typography
                                  sx={{
                                    fontSize: { xs: "0.5rem", md: "0.75rem" },
                                    color: "#6b7280",
                                  }}
                                >
                                  {currencySymbol} {order.final_price}
                                </Typography>
                              </Box>
                            </Grid>

                            {/* Order ID */}
                            <Grid item xs md={3}>
                              <Typography
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.7rem" },
                                  color: "#374151",
                                  fontWeight: 600,
                                }}
                              >
                                Order Id
                              </Typography>

                              <Typography
                                sx={{
                                  fontSize: { xs: "0.5rem", md: "0.75rem" },
                                  color: "#6b7280",
                                  wordBreak: "break-word",
                                }}
                              >
                                #{order.order_id}
                              </Typography>
                            </Grid>

                            {/* Status */}
                            <Grid item xs md={3} textAlign="center">
                              {/* <Typography
                                sx={{
                                  fontSize: { xs: "0.6rem", md: "0.7rem" },
                                  color: "#374151",
                                  fontWeight: 600,
                                }}
                              >
                                Status
                              </Typography> */}

                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.2,
                                  ...getStatusChipStyle(order.delivery_status_type),
                                  px: 0.8,
                                  py: "2px",
                                  borderRadius: "4px",
                                  fontSize: { xs: "0.55rem", md: "0.7rem" },
                                  fontWeight: 500,
                                  mt: 0.5,
                                }}
                              >
                                {/* <Box
                                  sx={{
                                    width: 4,
                                    height: 4,
                                    borderRadius: "50%",
                                    background: "#2e7d32",
                                  }}
                                /> */}
                                {order.delivery_status_type}
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>

                        <CardContent
                          sx={{
                            background: "#fff",
                            px: { xs: 2, md: 3 },
                            py: 2,
                          }}
                        >
                          <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={3} md={1.5}>
                              {_.get(
                                order,
                                "product_detail.images[0].image",
                              ) ? (
                                <img
                                  src={_.get(
                                    order,
                                    "product_detail.images[0].image",
                                  )}
                                  alt="product"
                                  style={{
                                    width: "100%",
                                    borderRadius: "8px",
                                    objectFit: "cover",
                                  }}
                                />
                              ) : (
                                <Box
                                  sx={{
                                    width: "100%",
                                    height: 70,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#f3f4f6",
                                    borderRadius: "8px",
                                  }}
                                >
                                  <RemoveShoppingCartIcon
                                    sx={{ color: "#9ca3af" }}
                                  />
                                </Box>
                              )}
                            </Grid>
                            <Grid item xs={9} md={7}>
                              {/* TITLE */}
                              <Typography fontWeight={600} fontSize="0.85rem">
                                {_.get(order, "product_detail.product_name") ||
                                  `Product ID: ${order.product_id}`}
                                {_.get(
                                  order,
                                  "product_detail.category_name",
                                ) && (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      fontSize: "0.85rem",
                                    }}
                                  >
                                    {" "}
                                    -{" "}
                                    {_.get(
                                      order,
                                      "product_detail.category_name",
                                    )}
                                  </span>
                                )}
                              </Typography>

                              {/* QTY + PRICE */}
                              <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
                                <Typography
                                  sx={{
                                    fontSize: "0.85rem",
                                    background: "#f3f4f6",
                                    px: 1,
                                    py: "2px",
                                    borderRadius: "4px",
                                    color: "#374151",
                                  }}
                                >
                                  Qty: {_.get(order, "quantity")}
                                </Typography>

                                <Typography
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "#111827",
                                    fontWeight: 500,
                                  }}
                                >
                                  {currencySymbol}{" "}
                                  {_.get(order, "unit_price", 0)}
                                </Typography>
                              </Box>

                              {/* PAYMENT */}
                              <Typography
                                sx={{
                                  fontSize: "0.85rem",
                                  color: "#6b7280",
                                  mt: 1,
                                }}
                              >
                                Payment mode:{" "}
                                <span style={{ color: "#374151" }}>
                                  {_.get(
                                    order,
                                    "payment_mode.payment_method_name",
                                  )}
                                </span>
                              </Typography>

                              {/* STATUS */}
                              <Typography
                                sx={{
                                  fontSize: "0.85rem",
                                  color: "#6b7280",
                                  mt: 0.5,
                                }}
                              >
                                Order Status:{" "}
                                <span
                                  style={{
                                    color: getStatusChipStyle(
                                      _.get(order, "delivery_status_type")
                                    ).color,
                                    fontWeight: 500,
                                  }}
                                >
                                  {_.get(order, "delivery_status_type")}
                                </span>
                              </Typography>
                            </Grid>

                            <Grid item xs={12} md={3} textAlign="right">
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: { xs: 2, md: 0 } }}>
                              {order.delivery_status ? (
                                <Button
                                  fullWidth
                                  sx={{
                                    px: 4,
                                    fontSize: "0.8rem",
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                    borderColor: "#d1d5db",
                                    color: "#111827",
                                    "&:hover": {
                                      borderColor: "#111827",
                                      background: "#f9fafb",
                                    },
                                  }}
                                  onClick={() => {
                                    resetCollapseDiv();
                                    if (
                                      displayOrder?.id !== order.id ||
                                      (displayOrder?.id === order.id &&
                                        isShippingAddress)
                                    ) {
                                      setDisplayOrder(order);
                                      showCollapseDiv(true);
                                      showShippingAddress(false);
                                      showStatus(true);
                                    }
                                  }}
                                  disabled={
                                    order?.delivery_status_type ===
                                    "Order Failed"
                                  }
                                  variant="outlined"
                                >
                                  Track Order
                                </Button>
                              ) : null}
                              {order.shipping_address ? (
                                <Button
                                  onClick={() => {
                                    resetCollapseDiv();
                                    if (
                                      displayOrder?.id !== order.id ||
                                      (displayOrder?.id === order.id &&
                                        isStatus)
                                    ) {
                                      setDisplayOrder(order);
                                      showCollapseDiv(true);
                                      showStatus(false);
                                      showShippingAddress(true);
                                    }
                                  }}
                                  variant="outlined"
                                  fullWidth
                                  sx={{
                                    px: 4,
                                    fontSize: "0.8rem",
                                    fontWeight: 500,
                                    textTransform: "uppercase",
                                    borderColor: "#d1d5db",
                                    color: "#111827",
                                    "&:hover": {
                                      borderColor: "#111827",
                                      background: "#f9fafb",
                                    },
                                  }}
                                >
                                  View Shipping Address
                                </Button>
                              ) : null}
                              </Box>
                            </Grid>
                          </Grid>
                        </CardContent>
                        <CardActions className="action-wrapper">
                          <Collapse
                            className="collapse-div"
                            in={collapseDiv && displayOrder.id == order.id}
                            timeout="auto"
                            unmountOnExit
                          >
                            {isShippingAddress
                              ? showAddress(
                                  _.get(displayOrder, "shipping_address"),
                                )
                              : ""}

                            {isStatus ? (
                              _.get(displayOrder, "delivery_status") == 10 ? (
                                <StatusStepper
                                  type="cancel"
                                  step={_.get(displayOrder, "delivery_status")}
                                />
                              ) : _.get(displayOrder, "delivery_status") ==
                                11 ? (
                                <StatusStepper
                                  type="fail"
                                  step={_.get(displayOrder, "delivery_status")}
                                />
                              ) : (
                                <StatusStepper
                                  step={_.get(displayOrder, "delivery_status")}
                                />
                              )
                            ) : (
                              ""
                            )}
                          </Collapse>
                          {/* <div className='collapsed-div'>

                                        </div> */}
                        </CardActions>
                        {/* <div className='action-wrapper'>
                                        <Button
                                            color="success"
                                            onClick={() => {
                                                selectOrder(order)
                                                addReviewToggle(true)
                                            }}
                                            variant="outlined">
                                            Add Review
                                        </Button>
                                    </div> */}
                      </Box>
                    ))}
                  </Grid>
                  <Grid item xs={12} sm={12} md={12} lg={10} xl={8}>
                    <TablePagination
                      rowsPerPageOptions={[]}
                      component="div"
                      count={totalCount}
                      rowsPerPage={totalCount < 10 ? totalCount : 10}
                      page={pageCount}
                      onPageChange={handleChangePage}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>
          </div>
          {/* <CustomDialog 
                isDialogOpen={true}
                className="formatted-dialog"
                header={DialogHeader}
                isFormattedDialog={false}
                setDialogOpen={(e) => {
                    addReviewToggle(e)
                }}
            > 
                <div className="dialog-body text-area-wrapper">
                    <Rating 
                        value={rating}
                        onChange={(event, newValue) => {
                          setRating(newValue);
                        }}
                    />
                    <TextField 
                        className="text-area"
                        margin="dense"
                        multiline
                        maxRows={3}
                        onChange={e => addReviewComment(e.target.value)}
                    />
                </div>
            </CustomDialog> */}
        </Box>
      </Container>
    </CustomLayout>
  );
};

export default OrderHistory;
